/**
 * accountprofile directives -> the maps the Technical Health tab renders from.
 *
 * The decision this module exists to encode: a stored override is AUTHORITATIVE.
 *
 * cma-api attaches `appliedDirectives` to an audit record only when the directive's
 * check_key is literally a key in that record's jobResults.checks (_directives.py).
 * The tab used to build its override map from those echoes alone — so an override on
 * a check the producer said nothing about that day was written to Cosmos, never
 * displayed, and looked to the vCIO exactly like a failed save. The saved row is the
 * source of truth; the echo is a bonus that wins where it exists, because only the
 * echo has been reconciled against the record the rest of the row is drawn from.
 *
 * Consequence worth knowing: a stored row carries no date, so an override applies on
 * every selected date, including before it was recorded. The server echo is already
 * date-blind, so this is consistent rather than new — it just becomes visible.
 */
import { CHECKS, overrideGrade, isOverrideExpired } from './healthRegistry';

const stripActor = (v) => String(v || '').replace('user:', '') || null;

/** Server echoes on audit records -> { checkKey: override }. */
export function indexEchoedOverrides(auditRecords, now = Date.now()) {
  const out = {};
  for (const r of auditRecords || []) {
    for (const ad of Array.isArray(r?.appliedDirectives) ? r.appliedDirectives : []) {
      if (ad.kind !== 'override' || !ad.checkKey) continue;
      if (!overrideGrade(ad.value)) continue;
      if (isOverrideExpired(ad.expires, now)) continue;
      out[ad.checkKey] = {
        id: null,
        value: ad.value,
        reason: ad.reason || null,
        override_expires: ad.expires || null,
        recorded_by: stripActor(ad.by),
      };
    }
  }
  return out;
}

/** Stored accountprofile rows -> { check_key: override }. Carries the row id. */
export function indexStoredOverrides(profileRows, now = Date.now()) {
  const out = {};
  for (const p of profileRows || []) {
    const d = p?.directive;
    if (!d || d.kind !== 'override' || !d.check_key) continue;
    if (!overrideGrade(d.value)) continue;
    if (isOverrideExpired(d.expires, now)) continue;
    out[d.check_key] = {
      id: p.id,
      value: d.value,
      // save() writes the vCIO's reason into `content`, never directive.reason.
      reason: p.content || null,
      override_expires: d.expires || null,
      // Who last edited the value, not the original author: the echo's `by` is the
      // creator, and attribution must not flip depending on whether a record exists.
      recorded_by: stripActor(p.last_updated_by || p.created_by),
    };
  }
  return out;
}

/** Echo wins on value; the stored row always supplies id and attribution. */
export function mergeOverrides(stored, echoed) {
  const out = { ...(stored || {}) };
  for (const [k, e] of Object.entries(echoed || {})) {
    const s = out[k];
    out[k] = { ...s, ...e, id: s?.id ?? null, recorded_by: s?.recorded_by ?? e.recorded_by };
  }
  return out;
}

export function buildOverrideMap({ profileRows, auditRecords, now = Date.now() }) {
  return mergeOverrides(
    indexStoredOverrides(profileRows, now),
    indexEchoedOverrides(auditRecords, now),
  );
}

// The two namespaces, spelled out once. override/suppress/circuit_capacity are keyed
// by the PRODUCER key (source.checkKey, what _directives.py matches against
// jobResults.checks); manual is keyed by the REGISTRY key (check.key), as manualRows.js
// also assumes. entitlement is keyed on addon_key and has no check_key at all.
const PRODUCER_CHECK_KEYS = new Set(CHECKS.map((c) => c.source.checkKey).filter(Boolean));
const REGISTRY_CHECK_KEYS = new Set(CHECKS.map((c) => c.key));

/**
 * Stored directives whose check_key names no check this page knows about. These can
 * never do anything — surface them rather than letting them sit silently.
 */
export function unmatchedDirectives(profileRows) {
  const out = [];
  for (const p of profileRows || []) {
    const d = p?.directive;
    if (!d || !d.kind || d.kind === 'entitlement') continue;
    const key = d.check_key;
    const valid = d.kind === 'manual' ? REGISTRY_CHECK_KEYS : PRODUCER_CHECK_KEYS;
    if (key && valid.has(key)) continue;
    out.push({ id: p.id, topic: p.topic, kind: d.kind, check_key: key || null });
  }
  return out;
}
